import type { BotDifficulty } from '@cribbage/bots';
import type { GameConfig, GameResult, Move, Seat, SeatResult } from '@cribbage/engine';
import { useSettingsStore } from '~/store/settingsStore.js';

/**
 * Finished offline games, kept on the device.
 *
 * Before this existed, a vs-bot or pass-and-play game left no trace anywhere:
 * `LocalSession` applied each move and dropped it, so there was nothing to
 * replay and nothing to count. Now every finished local game is stored here —
 * which is what makes a local history, a local replay, and (for vs-bot games
 * only) the sync in `offlineSync.ts` possible.
 *
 * Every record carries the player who played it, stamped when the game was
 * recorded. One device can hold games for several players over its life — the
 * anonymous player it started as, an account signed in on it, the fresh
 * anonymous player it gets when that account signs out — and each of them may
 * only read their own. So there is one table and every read filters on
 * `playerId`; the exception is the sync, which offers everything the device is
 * holding and lets the server decide what it may accept.
 *
 * IndexedDB rather than `localStorage`: a game is a few kilobytes of moves and
 * this list has no natural end, so it does not belong in the same 5MB bucket as
 * `cribbage.settings`. Written against the raw API — a wrapper this small is
 * smaller than any library that would do it.
 *
 * Only the moves are stored. The event log is five to seven times larger and
 * regenerable by re-running the engine, which is exactly what `ReplaySession`
 * does. See `docs/STATISTICS.md` §8.
 */

const DB_NAME = 'cribbage';
const DB_VERSION = 1;
const STORE = 'localGames';

/**
 * Bumped only if the stored shape changes in a way a reader must notice.
 *
 * v2 added `playerId` and replaced `countedFor` with `accepted`. v3 added the
 * forfeit fields to the stored result. This is the shape of the record on this
 * device; the replay format the upload declares is `REPLAY_FORMAT_VERSION` in
 * `@cribbage/protocol`, and the two are separate questions that used to share
 * this one number.
 */
export const LOCAL_FORMAT_VERSION = 3;

/**
 * The first version that stamped a player onto the record.
 *
 * Kept apart from `LOCAL_FORMAT_VERSION` because it answers a different
 * question. `claimUnownedGames` needs to know whether a record was written by a
 * client that *had* a `playerId` field and left it empty, or by one that never
 * had the field at all. That line was drawn at v2 and does not move when the
 * format is bumped for something else: comparing against the current version
 * would quietly turn every older-but-stamped record back into an orphan.
 */
const PLAYER_ID_FORMAT_VERSION = 2;

/**
 * Marks a `/history/:id` route as pointing at this device rather than the
 * server. Server ids are UUIDs, so the prefix can never collide with one.
 */
export const LOCAL_ID_PREFIX = 'local-';

export function localIdOf(routeId: string): string | null {
  return routeId.startsWith(LOCAL_ID_PREFIX) ? routeId.slice(LOCAL_ID_PREFIX.length) : null;
}

/** Pass-and-play never syncs: two humans, one session, no way to say whose it was. */
export type LocalGameKind = 'bot' | 'pass';

export interface LocalGameRecord {
  /** Client-generated, and the idempotency key the server dedupes on. */
  readonly id: string;
  readonly formatVersion: number;
  /** Missing on legacy records; never inferred from the installed engine. */
  readonly engineVersion?: string;
  /**
   * Who played it, stamped when the game was recorded rather than when it is
   * uploaded.
   *
   * That order is the whole point. Stamped at sync time, a game played signed
   * out landed on whoever happened to hold the token when the debounce fired,
   * so an evening of practice on a shared phone could arrive in a stranger's
   * statistics. Empty on a record written before the device had a player of
   * its own: those belong to nobody, and guessing an owner for them would be
   * the same mistake in a new place.
   */
  readonly playerId: string;
  readonly kind: LocalGameKind;
  readonly config: GameConfig;
  readonly seed: string;
  readonly moves: readonly { seat: Seat; move: Move }[];
  readonly seatNames: readonly string[];
  readonly botDifficulty: BotDifficulty | null;
  /** The signed-in player's seat, or null when nobody can be credited. */
  readonly mySeat: Seat | null;
  readonly startedAt: string;
  readonly finishedAt: string;
  /**
   * How it ended, or null while it is still being played.
   *
   * Kept so a list of a hundred games does not have to re-simulate a hundred
   * games to say who won. It is display data and stays here: the upload carries
   * moves only, and the server works the result out for itself.
   */
  readonly result: GameResult | null;
  /**
   * How many moves the server has acknowledged.
   *
   * A game is written here after every move and offered to the server as it is
   * played, so "sent" is a position in the log rather than a yes or no. Anything
   * past this mark is what the next push owes.
   */
  readonly syncedMoveCount: number;
  /** Set once the server has settled it, whether it counted or was refused. */
  readonly syncedAt: string | null;
  /**
   * True once the server has a row for this game.
   *
   * A refused game is settled but counted for nobody, and local history goes on
   * showing it — otherwise it would simply disappear, which is the one outcome a
   * player would notice and not understand. An accepted one steps aside,
   * because the server's copy is the one with a rating and a stable id: see
   * `isLocallyVisible`.
   */
  readonly accepted: boolean;
}

/**
 * A record as it came off the disk, which may predate `playerId`.
 *
 * Two older shapes to bring forward. The oldest wrote a `counted` boolean; the
 * one after it wrote `countedFor`, naming which of two upload routes a game
 * went in by, back when a guest's games went to a different door and belonged
 * to an install rather than a person. There is one door now, so either mark
 * reads as accepted.
 *
 * Neither carried a player. A record with no `playerId` was written before this
 * device had one, and there is no honest way to say now whose it was, so it
 * belongs to nobody: no history lists it and no upload offers it. Inventing an
 * owner at read time is the sync-time attribution bug wearing a different hat.
 */
interface StoredLocalGame extends Omit<LocalGameRecord, 'accepted' | 'playerId' | 'result'> {
  readonly playerId?: string;
  readonly accepted?: boolean;
  readonly countedFor?: 'account' | 'guest' | null;
  readonly counted?: boolean;
  readonly result?: StoredGameResult | null;
}

/**
 * A result as it may have been written down, which can predate the forfeit.
 *
 * Every game stored before a forfeit was expressible was played to its end, so
 * the missing fields have one honest answer each rather than a guess: it was
 * `completed`, nobody walked out, and no seat forfeited.
 */
type StoredGameResult = Omit<GameResult, 'outcome' | 'forfeitedBy' | 'seats'> & {
  readonly outcome?: GameResult['outcome'];
  readonly forfeitedBy?: Seat | null;
  readonly seats: readonly StoredSeatResult[];
};

type StoredSeatResult = Omit<SeatResult, 'forfeited'> & { readonly forfeited?: boolean };

function adopt(stored: StoredLocalGame): LocalGameRecord {
  const { counted, countedFor, result, ...rest } = stored;
  return {
    ...rest,
    playerId: stored.playerId ?? '',
    syncedMoveCount: stored.syncedMoveCount ?? 0,
    accepted: stored.accepted ?? (counted === true || (countedFor ?? null) !== null),
    result: adoptResult(result ?? null),
  };
}

/**
 * Bring a stored result forward to the shape the screens read.
 *
 * `games_forfeited` is deliberately left out of the stats map. Stats are
 * re-derived server-side from the moves, so writing a zero here would be this
 * device inventing a figure it is not the author of. The screens read the
 * fields above, not the map.
 */
function adoptResult(result: StoredGameResult | null): GameResult | null {
  if (result === null) return null;
  return {
    ...result,
    outcome: result.outcome ?? 'completed',
    forfeitedBy: result.forfeitedBy ?? null,
    seats: result.seats.map((seat) => ({ ...seat, forfeited: seat.forfeited ?? false })),
  };
}

function open(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        // History reads newest-first; sync reads everything not yet sent.
        store.createIndex('finishedAt', 'finishedAt');
      }
    };
    request.onsuccess = () => {
      resolve(request.result);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('indexedDB open failed'));
    };
  });
}

async function run<T>(
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => IDBRequest,
): Promise<T> {
  const db = await open();
  return new Promise<T>((resolve, reject) => {
    const tx = db.transaction(STORE, mode);
    const request = work(tx.objectStore(STORE));
    request.onsuccess = () => {
      resolve(request.result as T);
    };
    request.onerror = () => {
      reject(request.error ?? new Error('indexedDB request failed'));
    };
    tx.oncomplete = () => {
      db.close();
    };
  });
}

/**
 * Is there anywhere to store a game?
 *
 * Private browsing and old WebViews can leave `indexedDB` missing or throwing.
 * A device that cannot keep its history is not a broken game, so every caller
 * treats storage as best-effort.
 */
export function localGamesAvailable(): boolean {
  try {
    return typeof indexedDB !== 'undefined' && indexedDB !== null;
  } catch {
    return false;
  }
}

/**
 * Newest first, which is the only order a history screen wants, and only the
 * games this player played.
 *
 * The filter is the point rather than a detail: a device holds games for every
 * player who has used it, and one player reading another's history is the
 * shared-iPad bug this redesign exists to close. A null player has no games,
 * which is the first second of a fresh install.
 */
export async function listLocalGames(
  playerId: string | null,
  limit = 50,
): Promise<LocalGameRecord[]> {
  if (playerId === null || playerId === '') return [];
  const all = await allLocalGames();
  return all.filter((game) => game.playerId === playerId).slice(0, limit);
}

/**
 * Can this game be picked up again? Only one still being played.
 *
 * A finished game has nothing left to play, and that includes a forfeit: the
 * table's only exit is "End game and return home", which records the loss. So
 * an unfinished record is a game the player walked away from some other way,
 * such as closing the tab or losing the app to the phone. Exported because it
 * is the rule worth testing, and it is testable without a database.
 */
export function isResumable(game: LocalGameRecord): boolean {
  return game.result === null && game.moves.length > 0;
}

/**
 * The game the home screen offers to resume: this player's newest, if it is
 * unfinished.
 *
 * Newest only. Starting a new game over an interrupted one is the player's
 * answer to it, so an older unfinished game is not dug back up.
 */
export async function resumableGame(playerId: string | null): Promise<LocalGameRecord | null> {
  const [latest] = await listLocalGames(playerId, 1);
  return latest !== undefined && isResumable(latest) ? latest : null;
}

/**
 * Every game on the device, whoever played it. Newest first.
 *
 * Only the sync wants this: a game recorded by a player who has since signed
 * out is still owed to the server, and the server authorizes it by the
 * device's own session history rather than by who is holding the token now.
 */
async function allLocalGames(): Promise<LocalGameRecord[]> {
  if (!localGamesAvailable()) return [];
  try {
    const all = await run<StoredLocalGame[]>('readonly', (store) => store.getAll());
    return all.map(adopt).sort((a, b) => b.finishedAt.localeCompare(a.finishedAt));
  } catch {
    return [];
  }
}

export async function getLocalGame(id: string): Promise<LocalGameRecord | null> {
  if (!localGamesAvailable()) return null;
  try {
    const stored = await run<StoredLocalGame | undefined>('readonly', (store) => store.get(id));
    return stored === undefined ? null : adopt(stored);
  } catch {
    return null;
  }
}

/**
 * One writer at a time per game.
 *
 * Every write here is a read-modify-write: the move log comes from the screen
 * and the sync bookkeeping comes from the server, and each writer has to carry
 * the other's fields through untouched. Two in flight at once lose one side's
 * work, because the second reads before the first writes and then writes its
 * stale copy back over it. That is not a rare interleaving — a sync
 * acknowledgement lands while the next move is being saved on every match that
 * is longer than one push, and it was reproduced 300 times out of 300 in
 * Chromium. `updateLocalGame` also reads and writes in one transaction,
 * which is what orders writers in different tabs; this chain keeps the order
 * within a tab explicit rather than leaving it to the database.
 *
 * A chain per id rather than one for the whole store, because two different
 * games have nothing to say to each other and a shared queue would make every
 * write wait on the slowest.
 */
const writing = new Map<string, Promise<unknown>>();

function queued<T>(id: string, work: () => Promise<T>): Promise<T> {
  const result = (writing.get(id) ?? Promise.resolve()).then(work);
  // The queue itself must never reject, or one failed write would strand every
  // write behind it. The caller still sees the real outcome.
  const settled = result.then(
    () => undefined,
    () => undefined,
  );
  writing.set(id, settled);
  void settled.then(() => {
    // Dropped once nothing is behind it, so the map does not grow all session.
    if (writing.get(id) === settled) writing.delete(id);
  });
  return result;
}

/**
 * Read a game, change it, and write it back with nothing able to interleave.
 *
 * `revise` returns the record to store, or null to leave the game alone. It is
 * called with what is on disk right now rather than with a copy read earlier,
 * which is the whole point: a caller that already read the record has read a
 * record that may since have moved on.
 */
export async function updateLocalGame(
  id: string,
  revise: (existing: LocalGameRecord | null) => LocalGameRecord | null,
): Promise<LocalGameRecord | null> {
  return queued(id, async () => {
    if (!localGamesAvailable()) return revise(null);
    try {
      return await reviseStored(id, revise);
    } catch {
      // A game that cannot be stored is not worth interrupting the player over.
      return null;
    }
  });
}

/**
 * The read, the change and the write in one `readwrite` transaction.
 *
 * `queued` orders the writers in this tab and cannot see another one, and a
 * resumed game can be open in two. Read and written apart, both tabs read the
 * same log, both passed the extension check, and the later write undid the
 * earlier one, a finished game included. IndexedDB runs `readwrite`
 * transactions on one store one at a time, across tabs, so nothing can land
 * between a read and a write made inside one. `revise` is synchronous, which is
 * what lets it run between them without the transaction committing early.
 */
async function reviseStored(
  id: string,
  revise: (existing: LocalGameRecord | null) => LocalGameRecord | null,
): Promise<LocalGameRecord | null> {
  const db = await open();
  return new Promise<LocalGameRecord | null>((resolve, reject) => {
    const tx = db.transaction(STORE, 'readwrite');
    const store = tx.objectStore(STORE);
    let next: LocalGameRecord | null = null;
    const read = store.get(id);
    read.onsuccess = () => {
      const stored = read.result as StoredLocalGame | undefined;
      try {
        next = revise(stored === undefined ? null : adopt(stored));
      } catch (error) {
        reject(error instanceof Error ? error : new Error('revise failed'));
        tx.abort();
        return;
      }
      if (next !== null) store.put(next);
    };
    read.onerror = () => {
      reject(read.error ?? new Error('indexedDB read failed'));
    };
    tx.oncomplete = () => {
      db.close();
      resolve(next);
    };
    tx.onabort = () => {
      db.close();
      reject(tx.error ?? new Error('indexedDB transaction aborted'));
    };
  });
}

/**
 * Is this game waiting to be counted? Vs-bot, not yet sent, and with a seat to
 * credit.
 *
 * Pass-and-play fails this by its `kind`, which is where that rule belongs: it
 * is a property of the record rather than something each caller has to
 * remember. Exported because it is the rule worth testing, and it is testable
 * without a database.
 */
export function isPendingSync(game: LocalGameRecord): boolean {
  if (game.kind !== 'bot' || game.mySeat === null || game.syncedAt !== null) return false;
  // Nobody to file it under, so there is nothing to say in the upload. See
  // `playerId` above for why that stays true rather than being guessed at.
  if (game.playerId === '') return false;
  // Offered again only when there is more of it than the server has. Without
  // this a game still being played would be re-sent unchanged on every tick.
  return game.moves.length > (game.syncedMoveCount ?? 0);
}

/**
 * The record as it stands after the server acknowledged `count` of its moves.
 *
 * Exported because it is the rule worth testing, and it is testable without a
 * database.
 */
export function acknowledged(record: LocalGameRecord, count: number, at: string): LocalGameRecord {
  const read = Math.max(count, record.syncedMoveCount);
  /*
   * Finished with only when the answer covers everything we hold.
   *
   * A response is about the moves that went out with it, and more can be played
   * while it is in the air. Reading the result alone settled a game on an answer
   * about the ten moves before the eleventh one ended it: the record went quiet,
   * and the server kept a ten-move prefix of a game that had a winner.
   */
  const complete = record.result !== null && read >= record.moves.length;
  return {
    ...record,
    syncedMoveCount: read,
    syncedAt: complete ? at : null,
    accepted: complete,
  };
}

/**
 * Note how far the server has read, and settle the games it has all of.
 *
 * A push of a game still in play is an update rather than a conclusion: the
 * server has it up to here, and there will be more.
 */
export async function markSynced(
  progress: ReadonlyMap<string, number>,
  at = new Date().toISOString(),
): Promise<void> {
  if (!localGamesAvailable() || progress.size === 0) return;
  for (const [id, count] of progress) {
    await updateLocalGame(id, (record) =>
      record === null ? null : acknowledged(record, count, at),
    );
  }
}

/**
 * Everything the device still owes the server, whoever recorded it.
 *
 * Deliberately not filtered to the *current* player. A game played signed out is
 * still that anonymous player's game, and it goes up stamped with them: the
 * server accepts a player who has held a session on this device, so signing in
 * before the sync runs no longer redirects the game to the account.
 *
 * It is filtered to players who have not opted out, which is a different
 * question and the one the setting actually asks. See `syncOptOutPlayerIds`.
 */
export async function pendingSync(): Promise<LocalGameRecord[]> {
  const all = await allLocalGames();
  const optedOut = new Set(useSettingsStore.getState().syncOptOutPlayerIds);
  return all.filter((game) => isPendingSync(game) && !optedOut.has(game.playerId));
}

/**
 * Mark games the server has finished with, so they stop being offered.
 *
 * `accepted` says whether a row now exists on the server. Both answers settle
 * the game; only one of them means it is listed anywhere else.
 */
export async function markSettled(
  ids: readonly string[],
  accepted: boolean,
  at = new Date().toISOString(),
): Promise<void> {
  if (!localGamesAvailable() || ids.length === 0) return;
  for (const id of ids) {
    await updateLocalGame(id, (record) =>
      record === null ? null : { ...record, syncedAt: at, accepted },
    );
  }
}

/**
 * Games worth showing in a local history list: everything the list above them
 * does not already have.
 *
 * The server's copy is the one with a rating, an opponent's name and a stable
 * id, so an accepted game steps aside for it. Anonymous players have server
 * history too now, so this no longer turns on whether there is an account: the
 * only question is whether the server took the game.
 *
 * A refused game and a pass-and-play game are never anywhere else, so both stay
 * on this list for good.
 */
export function isLocallyVisible(game: LocalGameRecord): boolean {
  // A game still being played is not history yet. It is written after every move
  // so it can be logged as it happens, and the list of games you have played is
  // not where a game you are playing belongs.
  if (game.result === null) return false;
  return !game.accepted;
}

/**
 * Give the games nobody owns to the player this device has just been given.
 *
 * There is one moment when an unowned record has exactly one candidate owner,
 * and this is it: the device has just registered for the first time, so every
 * game already on it was played on this device before it had a player, by the
 * person now holding it. Attributing them anywhere else would be guesswork, and
 * that is why this runs at that moment and at no other. Called after any later
 * registration it would be the sync-time attribution bug wearing a hat.
 *
 * The case it exists for is a first launch with no network. The registration
 * fails, `playerId()` is null, and everything played that evening is stamped
 * with nobody. Without this those games are listed for no player and offered to
 * no server: played, stored, and lost.
 *
 * Returns how many it claimed, because a silent zero and a silent hundred look
 * the same from the caller.
 */
export async function claimUnownedGames(playerId: string): Promise<number> {
  if (playerId === '' || !localGamesAvailable()) return 0;
  /*
   * `formatVersion` is what separates the two ways a record can be unowned.
   * A current-format record with no player was written by this client when
   * registration had not happened yet, and the person now holding the device is
   * the only candidate. A record from before the field existed is unowned
   * because nothing ever stamped it: an install upgrading from the guest model
   * arrives with a whole history like that, and `readToken()` is null for it, so
   * it reaches this function on a first-ever enrolment. Claiming those would
   * hand a stranger's evening to a brand-new player, which is the guess this
   * function's own docstring refuses to make.
   */
  const unowned = (await allLocalGames()).filter(
    (game) => game.playerId === '' && game.formatVersion >= PLAYER_ID_FORMAT_VERSION,
  );
  for (const game of unowned) {
    // Through the write queue, not around it: a game still being played is
    // being saved after every move, and this must not land on top of one.
    await updateLocalGame(game.id, (record) =>
      record?.playerId === '' ? { ...record, playerId } : null,
    );
  }
  return unowned.length;
}

/**
 * Used when a player turns sync off and wants what is already here gone.
 *
 * Theirs only. The store holds games for every player who has used this device,
 * and one player's answer to "count my bot games" is not an instruction about
 * anybody else's history.
 */
export async function clearLocalGames(playerId: string | null): Promise<void> {
  if (playerId === null || playerId === '' || !localGamesAvailable()) return;
  try {
    const mine = await listLocalGames(playerId, Number.MAX_SAFE_INTEGER);
    for (const game of mine) await run('readwrite', (store) => store.delete(game.id));
  } catch {
    // Nothing to do: there is no state to leave inconsistent.
  }
}
